<template>
  <div class="card mb-25 border-0 rounded-0 bg-white letter-spacing">
    <div class="card-body p-15 p-sm-20 p-md-25 api-doc">
      <div v-if="loading" class="text-muted py-4">
        <span class="spinner-border spinner-border-sm me-2" role="status"></span>Chargement de la documentation…
      </div>
      <div v-else-if="error" class="doc-error" role="alert">
        <i class="ph ph-warning-circle" aria-hidden="true"></i> {{ error }}
      </div>

      <template v-else-if="spec">
        <header class="doc-intro">
          <div class="doc-intro-top">
            <h2 class="doc-h1">{{ spec.title }}</h2>
            <div class="doc-downloads">
              <button type="button" class="btn btn-sm btn-primary" :disabled="!!downloading" @click="download('pdf')">
                <span v-if="downloading === 'pdf'" class="spinner-border spinner-border-sm me-1" role="status"></span>
                <i v-else class="ph ph-file-pdf me-1" aria-hidden="true"></i>
                Télécharger PDF
              </button>
              <button type="button" class="btn btn-sm btn-outline-primary" :disabled="!!downloading" @click="download('postman')">
                <span v-if="downloading === 'postman'" class="spinner-border spinner-border-sm me-1" role="status"></span>
                <i v-else class="ph ph-paper-plane-tilt me-1" aria-hidden="true"></i>
                Collection Postman
              </button>
            </div>
          </div>
          <p>{{ spec.intro }}</p>
          <slot name="intro" />
        </header>

        <nav class="doc-toc" aria-label="Sommaire">
          <a v-for="s in sections" :key="s.id" :href="`#${s.id}`" @click.prevent="scrollTo(s.id)">{{ s.label }}</a>
        </nav>

        <section id="doc-endpoint" class="doc-section">
          <h3 class="doc-h2">1. Endpoint</h3>
          <dl class="doc-kv">
            <div><dt>URL</dt><dd><code>{{ spec.endpoint }}</code></dd></div>
            <div><dt>Méthode</dt><dd><span class="badge text-outline-primary rounded-pill">{{ spec.method }}</span></dd></div>
            <div><dt>Format</dt><dd>{{ spec.format }}</dd></div>
            <div v-if="spec.statuses_endpoint"><dt>Statuts</dt><dd><span class="badge text-outline-primary rounded-pill me-2">GET</span><code>{{ spec.statuses_endpoint }}</code></dd></div>
            <div><dt>Limite</dt><dd>{{ spec.rate_limit }} requêtes par minute et par adresse IP (au-delà : HTTP 429)</dd></div>
          </dl>
        </section>

        <section id="doc-auth" class="doc-section">
          <h3 class="doc-h2">2. Authentification</h3>
          <p>{{ spec.auth.intro }}</p>
          <CodeBlock :code="spec.auth.header" />
          <ul class="doc-list">
            <li v-for="(n, i) in spec.auth.notes" :key="i">{{ n }}</li>
          </ul>
        </section>

        <section id="doc-params" class="doc-section">
          <h3 class="doc-h2">3. Paramètres</h3>
          <div class="table-responsive">
            <table class="table doc-table">
              <thead>
                <tr><th scope="col">Champ</th><th scope="col">Type</th><th scope="col">Obligatoire</th><th scope="col">Description</th></tr>
              </thead>
              <tbody>
                <tr v-for="p in spec.params" :key="p.name">
                  <td><code>{{ p.name }}</code></td>
                  <td>{{ p.type }}</td>
                  <td>
                    <span v-if="p.required === 'yes'" class="badge text-outline-danger rounded-pill">Oui</span>
                    <span v-else-if="p.required === 'no'" class="badge text-outline-secondary rounded-pill">Non</span>
                    <span v-else class="badge text-outline-warning rounded-pill">Selon statut</span>
                  </td>
                  <td>{{ p.description }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section id="doc-statuses" class="doc-section">
          <h3 class="doc-h2">4. Statuts acceptés</h3>
          <ul class="doc-list">
            <li v-for="(r, i) in spec.status_rules" :key="i">{{ r }}</li>
          </ul>
          <div class="table-responsive">
            <table class="table doc-table">
              <thead>
                <tr><th scope="col">Code <code>statut</code></th><th scope="col">Libellé</th><th scope="col">comment</th><th scope="col">reported_date</th></tr>
              </thead>
              <tbody>
                <tr v-for="st in spec.statuses" :key="st.code">
                  <td><code>{{ st.code }}</code></td>
                  <td>{{ st.name }}</td>
                  <td>{{ st.comment_required ? "Obligatoire" : "Optionnel" }}</td>
                  <td>{{ st.reported_date_required ? "Obligatoire" : "—" }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section id="doc-format" class="doc-section">
          <h3 class="doc-h2">5. Requête et réponse</h3>
          <h4 class="doc-h3">Requête</h4>
          <CodeBlock :code="spec.request_example" />
          <h4 class="doc-h3">Réponse — succès (HTTP 200)</h4>
          <CodeBlock :code="spec.success_example" />
          <p class="doc-note">{{ spec.success_notes }}</p>
          <h4 class="doc-h3">Réponse — erreur</h4>
          <CodeBlock :code="spec.error_example" />
        </section>

        <section id="doc-errors" class="doc-section">
          <h3 class="doc-h2">6. Codes d'erreur</h3>
          <div class="table-responsive">
            <table class="table doc-table">
              <thead>
                <tr><th scope="col">HTTP</th><th scope="col">EDIT-STATUT.MESSAGE</th><th scope="col">Signification</th></tr>
              </thead>
              <tbody>
                <tr v-for="e in spec.errors" :key="e.http + e.code">
                  <td>{{ e.http }}</td>
                  <td><code>{{ e.code }}</code></td>
                  <td>{{ e.label }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p class="doc-note">{{ spec.error_notes }}</p>
        </section>

        <section id="doc-examples" class="doc-section">
          <h3 class="doc-h2">7. Exemples</h3>
          <div v-for="ex in spec.examples" :key="ex.title">
            <h4 class="doc-h3">{{ ex.title }}</h4>
            <CodeBlock :code="ex.code" />
          </div>
        </section>

        <section id="doc-trace" class="doc-section">
          <h3 class="doc-h2">8. Traçabilité</h3>
          <ul class="doc-list">
            <li v-for="(t, i) in spec.trace" :key="i">{{ t }}</li>
          </ul>
        </section>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, h, onMounted, ref } from "vue";
import api from "@/utils/axios";
import { useAppToast } from "@/composables/useAppToast";

/*
 * Global documentation of the driver status API. The content comes from the backend
 * (App\Support\DriverStatusApiDoc), like the PDF and the Postman collection.
 * This component is duplicated in the admin and livreur fronts: keep them identical.
 */

interface DocSpec {
  title: string;
  intro: string;
  endpoint: string;
  method: string;
  format: string;
  rate_limit: number;
  statuses_endpoint?: string;
  auth: { intro: string; header: string; notes: string[] };
  params: { name: string; type: string; required: string; description: string }[];
  editable_statuses: string[];
  statuses: { code: string; name: string; comment_required: boolean; reported_date_required: boolean }[];
  status_rules: string[];
  request_example: string;
  success_example: string;
  success_notes: string;
  error_example: string;
  errors: { http: number; code: string; label: string }[];
  error_notes: string;
  examples: { title: string; code: string }[];
  trace: string[];
}

/** <pre> code sample with a copy button. */
const CodeBlock = defineComponent({
  props: { code: { type: String, required: true } },
  setup(props) {
    const copied = ref(false);
    const copy = async () => {
      try {
        await navigator.clipboard.writeText(props.code);
        copied.value = true;
        setTimeout(() => (copied.value = false), 1500);
      } catch {
        /* clipboard unavailable: the code stays selectable */
      }
    };
    return () =>
      h("div", { class: "doc-codewrap" }, [
        h("button", { type: "button", class: "btn btn-sm btn-light doc-copy", onClick: copy }, copied.value ? "Copié" : "Copier"),
        h("pre", { class: "doc-code" }, [h("code", props.code)]),
      ]);
  },
});

export default defineComponent({
  name: "DriverStatusApiDocs",
  components: { CodeBlock },
  props: {
    // documentation endpoints: <url>, <url>/pdf, <url>/postman
    url: { type: String, default: "/api/auth/driver-status-api/docs" },
    // admin only: tells the backend the public API URL the front uses
    sendBaseUrl: { type: Boolean, default: false },
  },
  setup(props) {
    const { showToast } = useAppToast();
    const loading = ref(false);
    const error = ref("");
    const spec = ref<DocSpec | null>(null);
    const downloading = ref<"" | "pdf" | "postman">("");

    const sections = [
      { id: "doc-endpoint", label: "Endpoint" },
      { id: "doc-auth", label: "Authentification" },
      { id: "doc-params", label: "Paramètres" },
      { id: "doc-statuses", label: "Statuts" },
      { id: "doc-format", label: "Requête / réponse" },
      { id: "doc-errors", label: "Codes d'erreur" },
      { id: "doc-examples", label: "Exemples" },
      { id: "doc-trace", label: "Traçabilité" },
    ];

    const params = () => (props.sendBaseUrl ? { base_url: String(api.defaults.baseURL || "") } : {});

    const fileName = (disposition: string | undefined, fallback: string) => {
      const match = /filename="?([^";]+)"?/i.exec(disposition || "");
      return match ? match[1] : fallback;
    };

    const download = async (kind: "pdf" | "postman") => {
      downloading.value = kind;
      try {
        const res = await api.get(`${props.url}/${kind}`, { params: params(), responseType: "blob" });
        const name = fileName(
          res.headers["content-disposition"],
          kind === "pdf" ? "documentation-api-changement-statut.pdf" : "api-livreur-changement-statut.postman_collection.json"
        );
        const href = URL.createObjectURL(res.data);
        const link = document.createElement("a");
        link.href = href;
        link.download = name;
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(href), 1000);
      } catch {
        showToast("Téléchargement impossible.", "error");
      } finally {
        downloading.value = "";
      }
    };

    const scrollTo = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

    onMounted(async () => {
      loading.value = true;
      try {
        const res = await api.get(props.url, { params: params() });
        spec.value = res.data;
      } catch (e: any) {
        error.value = e?.response?.data?.message || "Impossible de charger la documentation.";
      } finally {
        loading.value = false;
      }
    });

    return { loading, error, spec, sections, downloading, download, scrollTo };
  },
});
</script>

<style scoped>
.api-doc {
  max-width: 1040px;
  color: var(--ui-text);
}
.doc-intro-top {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--ui-space-3);
  margin-bottom: var(--ui-space-2);
}
.doc-h1 {
  font-size: var(--ui-fs-xl);
  font-weight: var(--ui-fw-bold);
  margin: 0;
}
.doc-downloads {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.doc-intro p {
  color: var(--ui-text-secondary);
  max-width: 75ch;
}
.doc-error {
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 12px 14px;
  border-radius: var(--ui-radius-md);
  background: var(--ui-danger-soft);
  color: var(--ui-text);
}
.doc-toc {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: var(--ui-space-4) 0 var(--ui-space-2);
}
.doc-toc a {
  padding: 4px 12px;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-pill);
  font-size: var(--ui-fs-sm);
  color: var(--ui-text-secondary);
  text-decoration: none;
}
.doc-toc a:hover {
  border-color: var(--ui-accent);
  color: var(--ui-accent-text);
}
.doc-section {
  padding-top: var(--ui-space-6);
  scroll-margin-top: calc(var(--ui-topbar-height, 64px) + 16px);
}
.doc-h2 {
  font-size: var(--ui-fs-lg);
  font-weight: var(--ui-fw-semibold);
  margin-bottom: var(--ui-space-3);
  padding-bottom: 6px;
  border-bottom: 1px solid var(--ui-border);
}
.doc-h3 {
  font-size: var(--ui-fs-md);
  font-weight: var(--ui-fw-semibold);
  margin: var(--ui-space-4) 0 var(--ui-space-2);
}
.doc-kv {
  display: grid;
  gap: 10px;
  margin: 0;
}
.doc-kv > div {
  display: grid;
  grid-template-columns: 110px 1fr;
  gap: 12px;
}
.doc-kv dt {
  font-weight: var(--ui-fw-semibold);
  color: var(--ui-text-muted);
}
.doc-kv dd {
  margin: 0;
  word-break: break-all;
}
.doc-list {
  padding-left: 1.2rem;
  color: var(--ui-text-secondary);
}
.doc-list li + li {
  margin-top: 4px;
}
.doc-note {
  font-size: var(--ui-fs-sm);
  color: var(--ui-text-muted);
}
.doc-table {
  font-size: var(--ui-fs-sm);
}
.doc-table td {
  vertical-align: top;
}
.api-doc code {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.92em;
}
:deep(.doc-codewrap) {
  position: relative;
  margin-bottom: var(--ui-space-3);
}
:deep(.doc-copy) {
  position: absolute;
  top: 8px;
  right: 8px;
}
:deep(.doc-code) {
  margin: 0;
  padding: 14px 16px;
  padding-right: 80px;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-md);
  background: var(--ui-surface-sunken);
  color: var(--ui-text);
  font-size: 13px;
  line-height: 1.55;
  white-space: pre;
  overflow-x: auto;
}
@media (max-width: 575.98px) {
  .doc-kv > div {
    grid-template-columns: 1fr;
    gap: 2px;
  }
}
</style>
